import { useState, useEffect } from 'react';
import { Brain, Sparkles, ArrowRight, Database, Shield, Code2, BarChart3, Cpu, Activity } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../tailwindcss/Tailwind.css';
import ingestionService from './api';
import QueryPanel from './components/QueryPanel';
import AgentPipeline from './components/AgentPipeline';

const PIPELINE_HIGHLIGHTS = [
  { icon: Brain,     label: 'Intent Classification',  desc: 'Supervisor parses your question into a typed Query Plan IR' },
  { icon: Database,  label: 'Schema Resolution',       desc: 'Joins resolved from a Neo4j knowledge graph; JoinGraph built' },
  { icon: Code2,     label: 'LLM SQL Composition',     desc: 'GPT-4o generates dialect-aware SQL with JOIN DISCIPLINE rules' },
  { icon: Shield,    label: 'Policy Enforcement',      desc: 'Column masking, row-level security and RBAC applied' },
  { icon: Cpu,       label: 'Live Execution',          desc: 'Validated SQL runs against your database via pyodbc' },
  { icon: Activity,  label: 'Confidence Scoring',      desc: 'Each pipeline stage contributes a weighted confidence signal' },
];

const QueryData = ({ sourceId, onTabSwitch }) => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);

  const preSelectedSourceId = sourceId || '';

  useEffect(() => {
    ingestionService.getSources()
      .then(data => setSources(data || []))
      .catch(() => setSources([]))
      .finally(() => setSourcesLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-blue-50/20">
      {/* Page header */}
      <div className="bg-gray-50 p-2 sm:p-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-end">
          
          <button
            onClick={() => navigate('/analyze')}
            className="hidden sm:flex items-center gap-2 text-sm text-primaryDark hover:text-primaryDark font-medium whitespace-nowrap"
          >
            Manage sources
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Pipeline highlights strip */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {PIPELINE_HIGHLIGHTS.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="h-4 w-4 text-primaryDark" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-700 leading-tight">{label}</p>
                  <p className="text-[11px] text-gray-400 leading-tight mt-0.5 hidden lg:block">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left: Query panel */}
          <div className="xl:col-span-2">
            <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg p-6">
              {sourcesLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-4 text-gray-700">Loading data sources...</p>
                </div>
              ) : (
                <QueryPanel
                  sourceId={preSelectedSourceId || undefined}
                  sources={sources}
                />
              )}
            </div>
          </div>

          {/* Right: Agent pipeline sidebar */}
          <div>
            <AgentPipeline sources={sources} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryData;
